import { useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import { CloseIcon } from "components/ui/icons/uploadbar/CloseIcon"
import { ErefStatusIcon } from "components/ui/customWebviewer/ElevationReferencesSidebar/icons/ErefStatusIcon"
import { PrevNextNav } from "components/ui/customWebviewer/shared/PrevNextNav/PrevNextNav"
import { SegmentedWithoutBg } from "components/ui/segmented/SegmentedWithoutBg/SegmentedWithoutBg"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { ButtonDanger } from "components/ui/buttons/ButtonDanger/ButtonDanger"
import { FooterBar } from "components/ui/customWebviewer/FooterBar/FooterBar"
import { useReviewable } from "components/ui/customWebviewer/FooterBar/useReviewable"
import { useDeleteErefComment, useUpdateErefComment, useUpdateErefStatus } from "hooks/api/quotation/quotations.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { useMessage } from "hooks/message.hook"
import { spliceErefIntoCache } from "helpers/quotationCache.helper"
import AnnotationService from "services/annotations.service"
import { DetailsTab } from "./layout/DetailsTab/DetailsTab"
import { ConfigurationTab } from "./layout/ConfigurationTab/ConfigurationTab"
import { useEntityMutationQueue } from "hooks/mutationQueue.hook"
import { erefQueueKey } from "./erefQueueKey"
import type { TEcall, TEref, TErefStatus, TErefWithFurniture, TFurniture } from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"
import style from "./ReferenceSidebar.module.scss"

const { sidebar, header, headerLeft, headerTitle, headerIcon, closeButton, content, segmentedWrapper, segmented } =
	style

type TabOption = "Details" | "Configurations"
const TAB_OPTIONS: TabOption[] = ["Details", "Configurations"]

type Props = {
	onClose?: () => void
	eref: TErefWithFurniture
	ecalls?: TEcall[]
	configuration?: TQuotationDetail["configuration"]
	onEcallClick?: (ecall: TEcall) => void
	onFurnitureClick?: (furniture: TFurniture) => void
	onPrevConnectedEref?: () => void
	onNextConnectedEref?: () => void
}

export const ReferenceSidebar = ({
	onClose,
	eref,
	ecalls = [],
	configuration,
	onEcallClick,
	onFurnitureClick,
	onPrevConnectedEref,
	onNextConnectedEref,
}: Props) => {
	const [segmentedOption, setSegmentedOption] = useState<TabOption>("Details")
	const [searchParams] = useSearchParams()
	const quotationId = Number(searchParams.get("quotationID"))
	const queryClient = useQueryClient()
	const { formatError } = useApiErrorFormatter()
	const { success } = useMessage()
	const { mutateAsync: updateErefStatusAsync, isPending: isStatusPending } = useUpdateErefStatus()
	const { mutateAsync: updateErefCommentAsync } = useUpdateErefComment()
	const { mutateAsync: deleteErefCommentAsync } = useDeleteErefComment()
	const { enqueue, versionRef } = useEntityMutationQueue(erefQueueKey(eref.id), eref.version)

	const applyCommentInCache = (commentValue: string | null, nextVersion: number) => {
		queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) =>
			spliceErefIntoCache(cached, {
				...eref,
				version: nextVersion,
				estimation_config_comment: commentValue,
			}),
		)
	}

	const commitNote = (note: string) => {
		if (!quotationId) return
		const trimmed = note.trim()

		enqueue(async () => {
			try {
				const response =
					trimmed === ""
						? await deleteErefCommentAsync({ quotationId, erefId: eref.id, version: versionRef.current })
						: await updateErefCommentAsync({
								quotationId,
								erefId: eref.id,
								body: { version: versionRef.current, comment: note },
							})
				const responseVersion = (response?.data as { version?: number } | undefined)?.version
				const nextVersion = typeof responseVersion === "number" ? responseVersion : versionRef.current + 1
				versionRef.current = nextVersion
				const commentValue = trimmed === "" ? null : note
				applyCommentInCache(commentValue, nextVersion)
				success(commentValue === null ? "Comment removed" : "Comment saved")
			} catch (e) {
				formatError(e)
				queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
			}
		})
	}

	const status: TErefStatus = eref.status ?? "work_in_progress"
	const isReviewed = status === "reviewed"
	const isInQuotation = status === "in_quotation"
	const hasExistingNote = (eref.estimation_config_comment ?? "").length > 0

	const updateStatus = (next: TErefStatus) => {
		enqueue(async () => {
			try {
				const response = await updateErefStatusAsync({
					quotationId,
					erefId: eref.id,
					body: { version: versionRef.current, status: next },
				})
				const updated = response?.data as TEref | undefined
				if (!updated) return
				if (typeof updated.version === "number") versionRef.current = updated.version
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) =>
					spliceErefIntoCache(cached, updated),
				)
				AnnotationService.updateErefEntityStatus(eref.id, next)
			} catch (e) {
				formatError(e)
				queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
			}
		})
	}

	const { notePanel, reviewButton } = useReviewable({
		isReviewed,
		isPending: isStatusPending,
		initialNote: eref.estimation_config_comment ?? "",
		onToggleReviewed: (next) => updateStatus(next ? "reviewed" : "work_in_progress"),
		onSubmitNote: commitNote,
	})

	return (
		<div className={sidebar} data-hotkey-sidebar tabIndex={-1}>
			<div className={header} data-hotkey-skip>
				<div className={headerLeft}>
					{onPrevConnectedEref && onNextConnectedEref && (
						<PrevNextNav
							onPrev={onPrevConnectedEref}
							onNext={onNextConnectedEref}
							prevAriaLabel="Previous connected elevation reference"
							nextAriaLabel="Next connected elevation reference"
						/>
					)}
					<span className={headerIcon}>
						<ErefStatusIcon status={status} />
					</span>
					<span className={headerTitle}>Elevation reference</span>
				</div>
				<button className={closeButton} onClick={onClose} aria-label="Close sidebar">
					<CloseIcon />
				</button>
			</div>
			<div className={content}>
				<div className={segmentedWrapper}>
					<SegmentedWithoutBg
						options={TAB_OPTIONS}
						onChange={(value) => setSegmentedOption(value as TabOption)}
						size="middle"
						className={segmented}
					/>
				</div>
				{segmentedOption === "Details" ? (
					<DetailsTab
						eref={eref}
						ecalls={ecalls}
						onCommitNote={commitNote}
						onEcallClick={onEcallClick}
						onFurnitureClick={onFurnitureClick}
					/>
				) : (
					<ConfigurationTab eref={eref} configuration={configuration} />
				)}
			</div>
			<FooterBar topSlot={isInQuotation || hasExistingNote ? null : notePanel}>
				{isInQuotation ? (
					<ButtonDanger onClick={() => updateStatus("reviewed")} submittable={!isStatusPending}>
						Remove from quotation
					</ButtonDanger>
				) : (
					<>
						{reviewButton}
						<ButtonPrimary
							variant="success"
							onClick={() => updateStatus("in_quotation")}
							submittable={!isStatusPending}
						>
							Add to quotation
						</ButtonPrimary>
					</>
				)}
			</FooterBar>
		</div>
	)
}
